import * as Sentry from '@sentry/angular';
import {
  ApplicationConfig,
  inject,
  Injector,
  LOCALE_ID,
  PLATFORM_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
  TransferState,
  ErrorHandler,
} from '@angular/core';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { languageLoaderBrowserFactory } from './services/language/language-loader.browser';
import {
  HTTP_INTERCEPTORS,
  HttpBackend,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { DEFAULT_LANG } from './constants';
import { Location } from '@angular/common';
import { provideRouter, Router, withRouterConfig } from '@angular/router';
import { CommonRequestInterceptor } from './interceptors/common-request.interceptor';
import { MaintenanceInterceptor } from './interceptors/maintenance.interceptor';
import { routeResolver } from './factories/route-resolver.function';
import { MaintenanceService } from './services/maintenance.service';
import { buildAllRoutes, buildBlogRoute } from './app.routes';
import { PageRegistryService } from './services/page-registry.service';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { provideClientHydration, withEventReplay, withIncrementalHydration } from '@angular/platform-browser';

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: LOCALE_ID,
      useValue: DEFAULT_LANG,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CommonRequestInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MaintenanceInterceptor,
      multi: true,
    },
    provideClientHydration(
      withEventReplay(),
      withIncrementalHydration(),
    ),
    provideAppInitializer(() => {
      const initializerFn = routeResolver(inject(Injector), inject(TransferState), inject(PLATFORM_ID), inject(Location), inject(PageRegistryService), inject(Router));
      return initializerFn();
    }),
    provideAppInitializer(() => inject(MaintenanceService).fetchAndSetMaintenanceInfo()),
    provideHttpClient(
      withInterceptorsFromDi(),
      withFetch(),
    ),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      buildAllRoutes(buildBlogRoute(), []),
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
    ),
    provideTranslateService({
      defaultLanguage: DEFAULT_LANG,
      loader: {
        provide: TranslateLoader,
        useFactory: languageLoaderBrowserFactory,
        deps: [HttpBackend, TransferState],
      },
    }),
    provideEnvironmentNgxMask({ validation: false }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
  ],
};
